import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { commentActions, httpRequestsOnSuccessActions } from 'store';
import { CREATE_COMMENT, EDIT_COMMENT } from '../../store/comments/comment.type';
import { FindLoad, FindSuccess, Images, useModal, useSecondaryModal, User } from 'utils';
import { CustomDelete } from '../modal';
import { CommentEditor } from './commentEditor';
import { CommentText } from './commentText';
import { avatarColor, fullName, initialsOf, textToMentionHtml } from './commentUtils';
import { renderTimeAgo } from '../../fragments/chat/constants';

// const IconReply = () => (
//     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
//         <polyline points="9 17 4 12 9 7" />
//         <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
//     </svg>
// );
const IconDots = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="12" cy="19" r="1.6" />
    </svg>
);
const IconFile = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

/**
 * A single comment plus its nested replies. Own comments get an edit/delete
 * menu; every comment can be replied to. Replies hang off the exact comment
 * being replied to (`parentComment = comment.id`), so the thread nests to any
 * depth — reply to a root, reply to that reply, and so on (a true tree).
 */
export const CommentItem = ({
    comment,
    depth = 0,
    requestParams,
    enableMentions = true,
    attachmentPurpose,
    confirmInSecondaryModal = false,
}) => {
    const dispatch = useDispatch();
    const { userInfo } = User();
    const { open } = useModal();
    const { openSecondary } = useSecondaryModal();

    const [replying, setReplying] = useState(false);
    const [editing, setEditing] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [replyResetSignal, setReplyResetSignal] = useState(0);
    const replySubmitted = useRef(false);
    const editSubmitted = useRef(false);
    const menuRef = useRef(null);

    const isOwn = comment?.creator?.id && userInfo?.id && comment.creator.id === userInfo.id;
    const replyLoading = FindLoad(CREATE_COMMENT);
    const editLoading = FindLoad(EDIT_COMMENT);
    const createSuccess = FindSuccess(CREATE_COMMENT);
    const editSuccess = FindSuccess(EDIT_COMMENT);

    // Close reply box once our own reply lands.
    useEffect(() => {
        if (createSuccess?.length && replySubmitted.current) {
            replySubmitted.current = false;
            setReplying(false);
            setReplyResetSignal((n) => n + 1);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_COMMENT));
        }
    }, [createSuccess]);

    // Close edit box once our own edit lands.
    useEffect(() => {
        if (editSuccess?.length && editSubmitted.current) {
            editSubmitted.current = false;
            setEditing(false);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_COMMENT));
        }
    }, [editSuccess]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        if (menuOpen) document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [menuOpen]);

    const submitReply = (text, attachments) => {
        replySubmitted.current = true;
        dispatch(
            commentActions.createComment(
                {
                    onModel: comment.onModel,
                    resourceId: comment.resourceId,
                    text,
                    // Nest under the exact comment being replied to (full tree).
                    parentComment: comment.id,
                    attachments,
                },
                requestParams,
            ),
        );
    };

    const submitEdit = (text) => {
        editSubmitted.current = true;
        dispatch(commentActions.editComment(comment.id, { text }, requestParams));
    };

    const confirmDelete = () => {
        setMenuOpen(false);
        // A thread mounted inside the primary modal must confirm in the
        // secondary one, or the confirm replaces the modal it came from.
        const openConfirm = confirmInSecondaryModal ? openSecondary : open;
        openConfirm(
            <CustomDelete
                info="Delete comment?"
                text="Are you sure you want to delete this comment? This action cannot be undone."
                handleDel={() => dispatch(commentActions.deleteComment(comment.id, requestParams))}
                actionType={'DELETE_COMMENT'}
                closeSecondaryModal={confirmInSecondaryModal}
            />,
        );
    };

    const name = fullName(comment?.creator) || 'Unknown';

    return (
        <div className={`comment-item ${depth > 0 ? 'comment-item-reply' : ''}`}>
            <div className="comment-item-avatar" style={{ background: avatarColor(comment?.creator?.id) }}>
                {initialsOf(comment?.creator)}
            </div>

            <div className="comment-item-main">
                <div className="comment-item-head">
                    <span className="comment-item-name">{name}</span>
                    {isOwn && <span className="comment-item-you">You</span>}
                    <span className="comment-item-time">{renderTimeAgo(comment?.createdAt)}</span>
                    {comment?.editHistory?.length > 0 && <span className="comment-item-edited">(edited)</span>}

                    {isOwn && !editing && (
                        <div className="comment-item-menu" ref={menuRef}>
                            <button type="button" className="comment-item-menu-btn" onClick={() => setMenuOpen((v) => !v)}>
                                <img src={Images.moreGrayIcon} alt="more" />
                            </button>
                            {menuOpen && (
                                <div className="comment-item-menu-list">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditing(true);
                                            setMenuOpen(false);
                                        }}>
                                        <img src={Images.edit} alt="edit" />
                                        <p>Edit</p>
                                    </button>
                                    <button type="button" className="danger" onClick={confirmDelete}>
                                        <img src={Images.redTrashLine} alt="delete" />
                                        <p>Delete</p>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {editing ? (
                    <CommentEditor
                        compact
                        autoFocus
                        submitLabel="Save"
                        placeholder="Edit comment..."
                        initialContent={textToMentionHtml(comment?.text, comment?.mentions)}
                        submitting={editLoading?.length}
                        enableAttachments={false}
                        enableMentions={enableMentions}
                        onSubmit={(text) => submitEdit(text)}
                        onCancel={() => setEditing(false)}
                    />
                ) : (
                    <>
                        <CommentText text={comment?.text} mentions={comment?.mentions} />

                        {comment?.attachments?.length > 0 && (
                            <div className="comment-item-files">
                                {comment.attachments.map((att, i) =>
                                    att.url ? (
                                        <a key={i} className="comment-file-chip" href={att.url} target="_blank" rel="noreferrer">
                                            <IconFile />
                                            <span>{att.originalName}</span>
                                        </a>
                                    ) : (
                                        <span key={i} className="comment-file-chip">
                                            <IconFile />
                                            <span>{att.originalName}</span>
                                        </span>
                                    )
                                )}
                            </div>
                        )}

                        <button type="button" className="comment-reply-btn" onClick={() => setReplying((v) => !v)}>
                            <img src={Images.replyIcon} alt="" />
                            {/*<IconReply />*/}
                            <span>Reply</span>
                        </button>
                    </>
                )}

                {replying && (
                    <div className="comment-reply-editor">
                        <CommentEditor
                            compact
                            autoFocus
                            submitLabel="Post"
                            placeholder={`Reply to ${name}...`}
                            submitting={replyLoading?.length}
                            resetSignal={replyResetSignal}
                            enableMentions={enableMentions}
                            attachmentPurpose={attachmentPurpose}
                            onSubmit={submitReply}
                            onCancel={() => setReplying(false)}
                        />
                    </div>
                )}

                {comment?.replies?.length > 0 && (
                    <div className="comment-item-replies">
                        {comment.replies.map((child) => (
                            <CommentItem
                                key={child.id}
                                comment={child}
                                depth={depth + 1}
                                requestParams={requestParams}
                                enableMentions={enableMentions}
                                attachmentPurpose={attachmentPurpose}
                                confirmInSecondaryModal={confirmInSecondaryModal}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
